import React, { useState, useEffect } from "react";
// import { useNavigate } from "react-router-dom"; // Assuming navigate is passed as a prop
import { 
    Container, 
    TextField, 
    Box, 
    Typography, 
    Button, 
    Alert, 
    Paper, 
    InputAdornment,
    IconButton,
    CssBaseline,
    Divider,
    Link,
    CircularProgress
} from "@mui/material";
import { 
    Email as EmailIcon, 
    Lock as LockIcon, 
    Visibility,
    VisibilityOff,
    ArrowForward,
    School,
    WifiOff,
    Error as ErrorIcon,
} from "@mui/icons-material";
import { jwtDecode } from "jwt-decode";
import authApi from "../authApi"; 

const Login = ({ setToken, setUsername, navigate }) => { 
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [errorType, setErrorType] = useState(""); 
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        setError("");
        setErrorType("");
        setLoading(true);

        if (!email.trim()) {
            setError("Email is required");
            setErrorType("email");
            setLoading(false);
            return;
        }

        if (!password.trim()) {
            setError("Password is required");
            setErrorType("password");
            setLoading(false);
            return;
        }

        try {
            console.log("Attempting login for email:", email);
            
            const response = await authApi.auth.post("/login", { 
                email: email.toLowerCase().trim(), 
                password: password.trim() 
            });
            
            console.log("Login response received (successful):", response?.data);
            
            // This section primarily handles scenarios where the API returns a 200 OK status 
            // but indicates a logical failure in the response body.
            if (!response || !response.data) {
                console.error("Invalid response structure from server (expected data object):", response);
                setError("Invalid response from server. Please try again.");
                setErrorType("server");
                setPassword("");
                setLoading(false); 
                return;
            }

            if (response.data.success === false) {
                console.error("Login marked as unsuccessful by API (success: false):", response.data);
                setError(response.data.message || "Login failed. Please check your credentials.");
                setErrorType(response.data.field || "server"); // Use 'field' if provided by API for specific error type
                setPassword("");
                setLoading(false);
                return;
            }

            if (!response.data.accessToken) {
                console.error("No access token in successful-looking response:", response.data);
                setError("Authentication failed: No access token received from server.");
                setErrorType("server");
                setPassword("");
                setLoading(false);
                return;
            }

            const { accessToken } = response.data;
            
            if (typeof accessToken !== 'string' || accessToken.length < 20) { // Increased min length for typical JWTs
                console.error("Invalid access token format or length:", {
                    type: typeof accessToken,
                    length: accessToken?.length
                });
                setError("Received an invalid authentication token from the server.");
                setErrorType("server");
                setPassword("");
                setLoading(false);
                return;
            }

            try {
                const decodedToken = jwtDecode(accessToken);
                console.log("Token decoded successfully:", decodedToken);
                
                if (!decodedToken || !decodedToken.user || !decodedToken.user.username) {
                    throw new Error("Invalid token structure: Missing essential user data or username.");
                }

                const currentTime = Date.now() / 1000;
                if (decodedToken.exp && decodedToken.exp < currentTime) {
                    throw new Error("Token is expired. Please log in again.");
                }

                console.log("Login validation successful, setting authentication state.");
                
                localStorage.setItem("accessToken", accessToken);
                if (setToken) setToken(accessToken);
                
                const extractedUsername = decodedToken.user.username;
                if (setUsername) setUsername(extractedUsername);
                
                console.log("Authentication state set, redirecting to /languages.");
                if (navigate) navigate("/languages");
                
            } catch (tokenError) {
                console.error("Token validation or processing failed:", tokenError);
                setError(`Authentication token error: ${tokenError.message}`);
                setErrorType("server");
                setPassword("");
                localStorage.removeItem("accessToken");
                if (setToken) setToken(null); 
                if (setUsername) setUsername(""); 
            }
            
        } catch (error) { 
            console.error("Login request failed (API error or network issue):", error);
            
            localStorage.removeItem("accessToken");
            if (setToken) setToken(null);
            if (setUsername) setUsername("");
            
            let errorMessage = "An unexpected error occurred. Please try again.";
            let errorFieldType = "server"; // Default error type
            
            if (error.response) { 
                console.log("Server error response details:", {
                    status: error.response.status,
                    data: error.response.data,
                });
                
                errorMessage = error.response.data?.message || `Login failed (Error ${error.response.status}).`;
                errorFieldType = error.response.data?.field || "server"; // Use API 'field' if available
                
                // Ensure errorFieldType is one of the expected values for UI logic
                if (!["email", "password", "server", "network", "all"].includes(errorFieldType)) {
                    errorFieldType = "server";
                }
                
            } else if (error.request) {
                console.log("Network error - no response received from server.");
                errorMessage = "Network error. Please check your internet connection and try again.";
                errorFieldType = "network";
            } else {
                console.log("Error in setting up login request:", error.message);
                errorMessage = `Failed to send login request: ${error.message}`;
                errorFieldType = "server";
            }
            
            setError(errorMessage);
            setErrorType(errorFieldType);
            setPassword(""); // Clear password on any error from the API for security/UX
        } finally {
            setLoading(false);
        }
    };

    const handleCreateAccountClick = (e) => {
        e.preventDefault();
        if (navigate) navigate('/signup');
    };

    const handleForgotPasswordClick = (e) => {
        e.preventDefault();
        if (navigate) navigate('/forgot-password');
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    // UPDATED: getAlertContent now shows an Alert for all error types
    const getAlertContent = () => {
        if (!error) return null;

        let icon = <ErrorIcon />;
        let severity = "error"; // Default severity
        let alertAction = null;
        const message = error; // The error message state

        if (errorType === "network") {
            icon = <WifiOff />;
            alertAction = (
                <Button 
                    color="inherit" 
                    size="small" 
                    onClick={(e) => {
                        e.preventDefault(); 
                        const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
                        e.currentTarget.closest('form')?.dispatchEvent(submitEvent);
                    }}
                    disabled={loading}
                >
                    Retry
                </Button>
            );
        } else if (errorType === "email" || errorType === "password") {
            // Field-specific errors will also show this general alert at the top.
            // The helperText on the field provides more targeted feedback.
            // Icon and severity remain 'error' by default.
        } else if (errorType === "server" || errorType === "all") {
            // General server errors, icon and severity remain 'error'.
        }


        return (
            <Alert 
                severity={severity}
                icon={icon}
                action={alertAction}
                sx={{ 
                    mb: 3, 
                    borderRadius: 1,
                    alignItems: 'center',
                    // Make text easier to read
                    '.MuiAlert-message': {
                        overflow: 'hidden', // Prevents long messages from breaking layout
                        wordBreak: 'break-word', // Ensures long words wrap
                    }
                }}
            >
                {message}
            </Alert>
        );
    };

    return (
        <Box 
            sx={{
                minHeight: 'calc(100vh - 64px)', 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(120deg, #f0f7ff 0%, #e1f0ff 100%)',
                p: { xs: 2, sm: 3 } // Responsive padding
            }}
        >
            <CssBaseline />
            <Container maxWidth="md">
                <Paper 
                    elevation={6} // Slightly more pronounced shadow
                    sx={{ 
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        borderRadius: 3, // More rounded corners
                        overflow: 'hidden',
                        boxShadow: '0 12px 35px rgba(0,0,0,0.12)' // Softer, larger shadow
                    }}
                >
                    <Box
                        sx={{
                            flex: '1',
                            padding: { xs: 3, sm: 4, md: 5 }, // Responsive padding
                            display: 'flex', 
                            flexDirection: 'column',
                            backgroundColor: 'background.paper' // Use theme background
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }} >
                            <School color="primary" sx={{ fontSize: { xs: 32, sm: 40 }, mr: 1.5 }} />
                            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', fontSize: { xs: '1.8rem', sm: '2.125rem' } }} >
                                ankiverse.com
                            </Typography>
                        </Box>
                        <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, color: 'text.primary', fontSize: { xs: '1.25rem', sm: '1.5rem'} }} >
                            Welcome Back
                        </Typography>
                        <Typography variant="body1" sx={{ mb: {xs: 2, sm:3, md: 4}, color: 'text.secondary' }} >
                            Sign in to continue your learning journey
                        </Typography>

                        {getAlertContent()} {/* Alert will now show for all error types */}

                        <Box 
                            component="form" 
                            onSubmit={handleSubmit} 
                            sx={{ width: '100%' }}
                            noValidate
                        >
                            <TextField
                                label="Email"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                type="email"
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                disabled={loading}
                                error={errorType === "email"}
                                helperText={errorType === "email" ? error : ""}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <EmailIcon color={errorType === "email" ? "error" : "primary"} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Password"
                                type={showPassword ? "text" : "password"}
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                disabled={loading}
                                error={errorType === "password"}
                                helperText={errorType === "password" ? error : ""}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LockIcon color={errorType === "password" ? "error" : "primary"} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={handleClickShowPassword}
                                                onMouseDown={handleMouseDownPassword}
                                                edge="end"
                                                disabled={loading}
                                                color="primary"
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ mb: 2 }}
                            />
                            
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 3, mt: 1 }}>
                                <Link
                                    component="button"
                                    type="button"
                                    variant="body2"
                                    onClick={handleForgotPasswordClick}
                                    disabled={loading}
                                    sx={{ 
                                        color: 'primary.main',
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' },
                                        opacity: loading ? 0.7 : 1,
                                    }}
                                >
                                    Forgot password?
                                </Link>
                                <Link
                                    component="button"
                                    type="button"
                                    variant="body2"
                                    onClick={handleCreateAccountClick}
                                    disabled={loading}
                                    sx={{ 
                                        color: 'primary.main',
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' },
                                        opacity: loading ? 0.7 : 1,
                                    }}
                                >
                                    Create account
                                </Link>
                            </Box>
                            
                            <Button 
                                type="submit" 
                                variant="contained" 
                                fullWidth 
                                disabled={loading}
                                size="large" // Make button larger
                                sx={{ 
                                    py: 1.5,
                                    borderRadius: '8px', // Standardized border radius
                                    fontSize: '1rem',
                                    textTransform: 'none', // Keep case as is
                                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.25)',
                                    transition: 'transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
                                    '&:hover': {
                                        transform: 'translateY(-1px)',
                                        boxShadow: '0 6px 16px rgba(25, 118, 210, 0.35)'
                                    },
                                    position: 'relative' 
                                }}
                            >
                                {loading ? (
                                    <>
                                        <CircularProgress 
                                            size={24} 
                                            color="inherit" 
                                            sx={{ 
                                                // Centering the spinner
                                                position: 'absolute',
                                                left: 'calc(50% - 12px)',
                                                top: 'calc(50% - 12px)',
                                            }}
                                        />
                                        <span style={{ visibility: 'hidden' }}>Sign In <ArrowForward sx={{ ml: 1 }} /></span> {/* Keep width for layout consistency */}
                                    </>
                                ) : (
                                    <>
                                        Sign In
                                        <ArrowForward sx={{ ml: 1, fontSize: '1.25rem' }} />
                                    </>
                                )}
                            </Button>
                        </Box>
                    </Box>

                    {/* Right Side Panel (Decorative) */}
                    <Box
                        sx={{
                            flex: { md: '0.8' }, // Adjust flex basis for medium screens up
                            bgcolor: 'primary.main',
                            display: { xs: 'none', md: 'flex' },
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'relative',
                            p: {sm: 4, md: 5},
                            background: 'linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)', // Updated gradient
                            overflow: 'hidden'
                        }}
                    >
                        {/* Abstract decorative elements */}
                        <Box sx={{ position: 'absolute', width: {md: 300, lg: 350}, height: {md:300, lg:350}, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0) 70%)', top: {md: -120, lg:-150}, right: {md: -120, lg:-150}, zIndex: 1 }} />
                        <Box sx={{ position: 'absolute', width: {md:250, lg:300}, height: {md:250, lg:300}, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0) 70%)', bottom: {md:-100, lg:-120}, left: {md:-100, lg:-120}, zIndex: 1 }} />
                        
                        <Typography variant="h5" align="center" sx={{ fontWeight: 600, color: 'white', mb: 3, zIndex: 2, fontSize: { md: '1.5rem', lg: '1.75rem'} }} >
                            Learn Better, Remember Longer
                        </Typography>
                        <Divider sx={{ bgcolor: 'rgba(255,255,255,0.35)', width: '50px', my: 2, zIndex: 2 }} />
                        <Typography variant="body1" align="center" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 300, mb: 4, lineHeight: 1.7, zIndex: 2, maxWidth: '90%' }} >
                            "The brain thrives on meaningful connections. Our adaptive learning system personalizes your experience, making every moment count."
                        </Typography>
                        <Box sx={{ bgcolor: 'rgba(255,255,255,0.1)', borderRadius: '50%', p: 2, mb: 3, zIndex: 2, boxShadow: '0 0 15px rgba(255,255,255,0.1)' }} >
                            <School sx={{ fontSize: {md: 50, lg: 60}, color: 'white' }} />
                        </Box>
                        <Typography variant="body2" align="center" sx={{ color: 'rgba(255,255,255,0.75)', fontStyle: 'italic', maxWidth: '85%', zIndex: 2 }} >
                            Unlock your full learning potential.
                        </Typography>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

// Example App structure for context (not part of the Login component itself)
// const App = () => {
//     const [token, setTokenState] = useState(localStorage.getItem("accessToken"));
//     const [username, setUsernameState] = useState("");
    
//     const navigate = (path) => {
//         console.log(`(Mock) Navigating to ${path}`);
//     };

//     useEffect(() => {
//         if (token) {
//             try {
//                 const decoded = jwtDecode(token);
//                 if (decoded.user && decoded.user.username) {
//                    setUsernameState(decoded.user.username);
//                 } else {
//                    throw new Error("Username not found in token");
//                 }
//             } catch (e) {
//                 console.error("Invalid token on app load:", e.message);
//                 localStorage.removeItem("accessToken");
//                 setTokenState(null);
//                 setUsernameState("");
//             }
//         } else {
//             // Ensure username is cleared if token is not present or removed
//             setUsernameState("");
//         }
//     }, [token]);
    
//     return (
//         <div>
//             <Login 
//                 setToken={setTokenState} 
//                 setUsername={setUsernameState} 
//                 navigate={navigate} 
//             />
//             {token && <p style={{textAlign: 'center', marginTop: '20px'}}>Logged in as: {username} (Token: {token.substring(0,20)}...)</p>}
//             {!token && <p style={{textAlign: 'center', marginTop: '20px'}}>Not logged in.</p>}
//         </div>
//     );
// };

export default Login; // Export the Login component
